import { useState } from "react";
import DOMPurify from "dompurify";
import {
    FaArchive, FaChevronDown, FaChevronRight, FaDownload, FaEye, FaEyeSlash, FaExpand,
    FaFileAlt, FaFileExport, FaPen, FaRegStar, FaStar,
} from "react-icons/fa";
import { AppButton } from "../../_CORE/APP/Templates/_UI/Button/_index";
import { AppCodeBlock } from "../../_CORE/APP/Templates/_UI/CodeBlock/_index";
import { Card, Badge } from "../../_CORE/APP/Templates/_UI/Kit/_index";
import { CardActionMenu } from "./CardActionMenu";
import { CopyValue } from "../Views/Private/_Projects/_UI/CopyValue";
import { downloadBlobFile, downloadFilesAsZip } from "./FileBatchDownload";
import { REACT_HOT_TOAST_ERROR } from "../../_CORE/HELPERS/reactHotToast.helper";
import "./_Styles/FileCollection.css";

const onHasRow = (entry, row) => {
    if (!row?.field) return false;
    if (row.isSecret) {
        const key = `has${row.field.charAt(0).toUpperCase()}${row.field.slice(1)}`;
        return !!entry?.[key];
    }
    return entry?.[row.field] !== null && entry?.[row.field] !== undefined && String(entry[row.field]).trim() !== "";
};

/*
 * Tarjeta canonica para una hija dentro de cualquier Baul.
 * Visualmente sigue el mismo patron que Credenciales de proyecto:
 * cabecera -> primeros campos -> Mostrar más -> detalle/archivos/notas -> acciones.
 */
export const VaultNestedEntryCard = ({
    entry,
    parentTitle,
    typeLabel,
    environmentLabel = "",
    rows = [],
    revealed = null,
    onReveal,
    onFavorite,
    onExport,
    onEdit,
    onArchive,
    onOpenFile,
    loadBlob,
    extraBadges = null,
    afterContent = null,
}) => {
    const [useExpanded, setExpanded] = useState(false);
    const [useDownloadingFiles, setDownloadingFiles] = useState(false);

    if (!entry) return null;

    const visibleRows = rows.filter((row) => onHasRow(entry, row));
    const primaryRows = visibleRows.slice(0, 5);
    const detailRows = visibleRows.slice(5);
    const extra = (entry.extra ?? []).filter((field) => String(field?.value ?? "").trim() !== "");
    const files = entry.files ?? [];

    const hasDetails = detailRows.length > 0
        || extra.length > 0
        || !!entry.command
        || !!entry.notes
        || files.length > 0
        || !!afterContent;

    const onDownloadFile = async (file) => {
        if (!file || typeof loadBlob !== "function") return;
        try {
            const blob = await loadBlob(file);
            if (!(blob instanceof Blob)) throw new Error("Archivo inválido");
            downloadBlobFile(blob, file.originalName || file.title || "archivo");
        } catch {
            REACT_HOT_TOAST_ERROR("No se pudo descargar el archivo");
        }
    };

    const onDownloadAllFiles = async () => {
        if (!files.length || typeof loadBlob !== "function" || useDownloadingFiles) return;
        setDownloadingFiles(true);
        try {
            await downloadFilesAsZip({
                files,
                loadBlob,
                fileName: `${entry.title || "credencial"}-archivos.zip`,
                successMessage: `${files.length} archivo${files.length === 1 ? "" : "s"} de la credencial descargado${files.length === 1 ? "" : "s"}`,
            });
        } finally {
            setDownloadingFiles(false);
        }
    };

    const onRenderRow = (row) => {
        const value = row.isSecret && revealed ? revealed[row.field] : entry[row.field];
        const canCopy = !row.isSecret || !!revealed;

        return (
            <div key={row.field} className="LStudio__app_credentials_item_row">
                <span className="LStudio__app_credentials_item_key">{row.label}</span>
                {canCopy
                    ? <CopyValue value={value} label={`Copiar ${row.label}`} />
                    : <span className="LStudio__app_credentials_item_hidden">{entry[row.field]}</span>}
            </div>
        );
    };

    return (
        <Card
            className="LStudio__app_credentials_item LStudio__app_credentials_nested_card LStudio__app_nested_vault_card"
            data-level="1"
            data-favorite={!!entry.isFavorite}>

            <div className="LStudio__app_credentials_nested_context LStudio__app_nested_vault_context">
                <span className="LStudio__app_credentials_nested_branch LStudio__app_nested_vault_branch" aria-hidden="true">↳</span>
                <span className="LStudio__app_credentials_nested_parent LStudio__app_nested_vault_parent">
                    Hija de <strong>{parentTitle}</strong>
                </span>
                <Badge tone="neutral" size="sm">Credencial hija</Badge>
            </div>

            <div className="LStudio__app_credentials_item_head" data-vault-entry-focus-id={entry.id}>
                {entry.isFavorite && <FaStar className="LStudio__app_credentials_item_star" title="Destacada" />}
                <span className="LStudio__app_credentials_item_title">{entry.title}</span>

                {typeLabel && <Badge tone="neutral" size="sm">{typeLabel}</Badge>}
                {environmentLabel && <Badge tone="accent" size="sm">{environmentLabel}</Badge>}
                {extraBadges}

                <CardActionMenu
                    title="Acciones de credencial"
                    ariaLabel={`Opciones de ${entry.title}`}
                    popoverClassName="LStudio__app_credentials_nested_popover LStudio__app_nested_vault_popover"
                    items={[
                        {
                            key: "favorite",
                            icon: entry.isFavorite ? <FaRegStar /> : <FaStar />,
                            label: entry.isFavorite ? "Quitar destacado" : "Destacar",
                            onClick: () => onFavorite?.(entry),
                        },
                        { key: "export", icon: <FaFileExport />, label: "Guardar credencial...", onClick: () => onExport?.(entry) },
                        { divider: true },
                        { key: "edit", icon: <FaPen />, label: "Editar", onClick: () => onEdit?.(entry) },
                        { divider: true },
                        { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onArchive?.(entry) },
                    ]}
                />
            </div>

            <div className="LStudio__app_credentials_item_body">
                {primaryRows.map(onRenderRow)}
            </div>

            {useExpanded && hasDetails && (
                <div className="LStudio__app_credentials_details">
                    {detailRows.length > 0 && (
                        <div className="LStudio__app_credentials_item_body">
                            {detailRows.map(onRenderRow)}
                        </div>
                    )}

                    {extra.length > 0 && (
                        <div className="LStudio__app_credentials_item_body">
                            {extra.map((field, index) => {
                                const value = field.isSecret && revealed
                                    ? (revealed.extra?.find((item) => item.label === field.label)?.value ?? field.value)
                                    : field.value;
                                return (
                                    <div key={`${field.label}_${index}`} className="LStudio__app_credentials_item_row">
                                        <span className="LStudio__app_credentials_item_key">{field.label}</span>
                                        {!field.isSecret || revealed
                                            ? <CopyValue value={value} label={`Copiar ${field.label}`} />
                                            : <span className="LStudio__app_credentials_item_hidden">{field.value}</span>}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {entry.command && (
                        <div className="LStudio__app_credentials_command">
                            <AppCodeBlock code={entry.command} language={entry.commandLanguage} title="Comando de conexión" isPretty={false} />
                        </div>
                    )}

                    {files.length > 0 && (
                        <div className="LStudio__app_credentials_files LStudio__app_file_collection">
                            <div className="LStudio__app_file_collection_header">
                                <span>{files.length} archivo{files.length === 1 ? "" : "s"}</span>
                                <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaDownload />}
                                    isLoading={useDownloadingFiles} onClick={onDownloadAllFiles}>
                                    Descargar todos
                                </AppButton>
                            </div>
                            <div className="LStudio__app_file_collection_scroll">
                                {files.map((file) => (
                                    <div key={file.id} className="LStudio__app_credentials_file">
                                        <FaFileAlt />
                                        <span className="LStudio__app_credentials_file_name">
                                            <strong>Archivo</strong>
                                            <small>{file.originalName || file.title}</small>
                                        </span>
                                        <AppButton
                                            size="sm"
                                            variant="ghost"
                                            tone="neutral"
                                            isIconOnly
                                            icon={<FaExpand />}
                                            aria-label="Vista previa"
                                            title="Vista previa"
                                            onClick={() => onOpenFile?.(file)}
                                        />
                                        <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly
                                            icon={<FaDownload />} aria-label="Descargar archivo" title="Descargar archivo"
                                            onClick={() => onDownloadFile(file)} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {entry.notes && (
                        <div className="LStudio__app_credentials_item_notes"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(entry.notes) }} />
                    )}

                    {afterContent}
                </div>
            )}

            {(onReveal || hasDetails) && (
                <div className="LStudio__app_credentials_item_actions">
                    {onReveal && (
                        <AppButton size="sm" variant="ghost" tone="neutral"
                            icon={revealed ? <FaEyeSlash /> : <FaEye />}
                            onClick={() => onReveal(entry)}>
                            {revealed ? "Ocultar" : "Revelar"}
                        </AppButton>
                    )}

                    {hasDetails && (
                        <AppButton size="sm" variant="ghost" tone="neutral"
                            icon={useExpanded ? <FaChevronDown /> : <FaChevronRight />}
                            onClick={() => setExpanded((current) => !current)}>
                            {useExpanded ? "Mostrar menos" : "Mostrar más"}
                        </AppButton>
                    )}
                </div>
            )}
        </Card>
    );
};
